import { ArrowDownTrayIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Button } from 'flowbite-react';
import React, { useMemo, useState } from 'react';

import { showToast } from '../../../../common/Notification';
import type { DpeGeneralMailTemplateKey } from './dpeGeneralMailContent';
import {
  buildDpeGeneralMailingText,
  DPE_GENERAL_MAIL_TEMPLATES,
  GENERAL_MAIL_STANDARD_OPTIONS,
  GENERAL_MAIL_TRANSACTION_OPTIONS,
} from './dpeGeneralMailContent';
import { generateDpeGeneralMailingDocx } from './dpeGeneralMailingDocx';

interface DpeGeneralMailingPreviewProps {
  coproName?: string | null;
  rdvUrl: string;
  referentFullName: string;
  syndicName: string;
  selectedTemplateKey: DpeGeneralMailTemplateKey;
  defaultTemplateKey?: DpeGeneralMailTemplateKey;
  onTemplateKeyChange: (key: DpeGeneralMailTemplateKey) => void;
  onConfirm?: (key: DpeGeneralMailTemplateKey) => void;
  confirming?: boolean;
}

type Tab = 'html' | 'text';

function DpeGeneralMailingPreview({
  coproName,
  rdvUrl,
  referentFullName,
  syndicName,
  selectedTemplateKey,
  defaultTemplateKey,
  onTemplateKeyChange,
  onConfirm,
  confirming = false,
}: DpeGeneralMailingPreviewProps) {
  const [activeTab, setActiveTab] = useState<Tab>('html');
  const [downloading, setDownloading] = useState(false);

  const textPayload = useMemo(
    () => buildDpeGeneralMailingText(selectedTemplateKey, referentFullName, syndicName, rdvUrl),
    [selectedTemplateKey, referentFullName, syndicName, rdvUrl],
  );

  const handleCopy = async() => {
    try {
      const contentToCopy = textPayload.subject
        ? `Objet : ${textPayload.subject}\n\n${textPayload.body}`
        : textPayload.body;
      await navigator.clipboard.writeText(contentToCopy);
      showToast('success', 'Corps du mail copié dans le presse-papiers.');
    } catch {
      showToast('error', 'Impossible de copier le corps du mail.');
    }
  };

  const handleDocx = async() => {
    setDownloading(true);
    try {
      await generateDpeGeneralMailingDocx({
        templateKey: selectedTemplateKey,
        rdvUrl,
        referentFullName,
        syndicName,
        coproName,
      });
    } catch (error) {
      console.error('DPE general mailing DOCX generation failed:', error);
      showToast('error', 'Impossible de générer le document Word.');
    } finally {
      setDownloading(false);
    }
  };

  const meta = DPE_GENERAL_MAIL_TEMPLATES[selectedTemplateKey];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="mb-4 border-l-4 border-blue-500 bg-blue-50 p-3 text-sm text-blue-900">
        <p className="font-medium">Comment envoyer ce mail&nbsp;?</p>
        <ol className="mt-1 ml-5 list-decimal space-y-0.5">
          <li>Choisissez le modèle qui convient le mieux à la copropriété&nbsp;: les variantes ci-dessous portent le même message institutionnel, avec deux modèles estimation sans notion de DPE.</li>
          <li>Cliquez sur <em>Copier le corps du mail</em> ou <em>Télécharger en .docx</em>.</li>
          <li>Transmettez le mail depuis la liste de diffusion du syndic.</li>
        </ol>
      </div>

      <div className="mb-4 border-l-4 border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-900">
        <strong>Rappel&nbsp;:</strong> le mail ne doit jamais mentionner la détection d&apos;un DPE.
        Les modèles proposés respectent déjà cette règle.
      </div>

      <div className="mb-2">
        <p className="text-sm font-semibold text-gray-700">Choisissez le modèle de mail</p>
      </div>

      <div className="mb-4 space-y-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {GENERAL_MAIL_STANDARD_OPTIONS.map((key) => {
            const isActive = key === selectedTemplateKey;
            const isDefault = defaultTemplateKey === key;
            return (
              <button
                key={key}
                type="button"
                className={`relative rounded-lg border px-3 py-2 text-left text-sm transition ${
                  isActive
                    ? 'border-purple-600 bg-purple-600 text-white shadow'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-purple-400'
                }`}
                onClick={() => onTemplateKeyChange(key)}
              >
                {isDefault && (
                  <span className="absolute -top-2 left-2 inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 shadow-sm">
                    <span role="img" aria-label="Étoile">★</span>
                    <span>Modèle par défaut</span>
                  </span>
                )}
                <span className="block font-semibold">{DPE_GENERAL_MAIL_TEMPLATES[key].label}</span>
                <span className="mt-0.5 block text-xs opacity-80">{DPE_GENERAL_MAIL_TEMPLATES[key].description}</span>
              </button>
            );
          })}
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">
            Référent transaction sans notion des DPEs
          </p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {GENERAL_MAIL_TRANSACTION_OPTIONS.map((key) => {
              const isActive = key === selectedTemplateKey;
              const isDefault = defaultTemplateKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`relative rounded-lg border px-3 py-2 text-left text-sm transition ${
                    isActive
                      ? 'border-purple-600 bg-purple-600 text-white shadow'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-purple-400'
                  }`}
                  onClick={() => onTemplateKeyChange(key)}
                >
                  {isDefault && (
                    <span className="absolute -top-2 left-2 inline-flex items-center gap-0.5 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 shadow-sm">
                      <span role="img" aria-label="Étoile">★</span>
                      <span>Modèle par défaut</span>
                    </span>
                  )}
                  <span className="block font-semibold">{DPE_GENERAL_MAIL_TEMPLATES[key].label}</span>
                  <span className="mt-0.5 block text-xs opacity-80">{DPE_GENERAL_MAIL_TEMPLATES[key].description}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mb-2 text-sm text-gray-600">
        <span className="font-semibold">Objet :</span>
        {' '}
        {meta.subject}
      </div>

      <div className="mb-3 flex gap-2 border-b border-gray-200">
        <button
          type="button"
          className={`px-3 py-2 text-sm font-medium ${activeTab === 'html' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-gray-500'}`}
          onClick={() => setActiveTab('html')}
        >
          Aperçu visuel
        </button>
        <button
          type="button"
          className={`px-3 py-2 text-sm font-medium ${activeTab === 'text' ? 'border-b-2 border-purple-600 text-purple-700' : 'text-gray-500'}`}
          onClick={() => setActiveTab('text')}
        >
          Texte brut
        </button>
      </div>

      <div className="mb-4 max-h-96 overflow-auto rounded border border-gray-200 bg-white p-4">
        {activeTab === 'html' ? (
          <div className="space-y-3 text-sm leading-relaxed text-gray-800">
            {meta.paragraphs.map((p) => (
              <p key={p.slice(0, 32)}>{p}</p>
            ))}
            {meta.bulletGroups.map((group) => (
              <div key={group.intro || group.items[0]}>
                {group.intro && <p className="font-semibold">{group.intro}</p>}
                <ul className="list-disc pl-6">
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
            <p>
              Prendre rendez-vous :
              {' '}
              <a className="text-purple-700 underline" href={rdvUrl} target="_blank" rel="noreferrer">
                {rdvUrl}
              </a>
            </p>
            <p>
              {referentFullName}
              <br />
              {`Référent Mutation Location${syndicName ? ` — ${syndicName}` : ''}`}
            </p>
          </div>
        ) : (
          <pre className="text-sm leading-relaxed whitespace-pre-wrap text-gray-800">{textPayload.body}</pre>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button color="purple" onClick={handleCopy}>
          <ClipboardDocumentIcon className="mr-2 h-5 w-5" />
          Copier le corps du mail
        </Button>
        <Button color="purple" onClick={handleDocx} disabled={downloading}>
          <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
          {downloading ? 'Génération…' : 'Télécharger en .docx'}
        </Button>
        {onConfirm && (
          <Button
            color="gray"
            onClick={() => onConfirm(selectedTemplateKey)}
            disabled={confirming}
          >
            {confirming ? 'Enregistrement…' : 'Mémoriser ce modèle pour cet envoi'}
          </Button>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-500">
        «&nbsp;Mémoriser ce modèle&nbsp;» enregistre la variante choisie sur cet envoi
        (utile si vous voulez la retrouver plus tard) — aucun mail n&apos;est expédié par cette action.
      </p>
    </div>
  );
}

export default DpeGeneralMailingPreview;
